import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ethers } from 'ethers';
import { NetworkProvider } from './NetworkContext';

interface WalletProviderInfo {
  provider: any;
  name: string;
  isAvailable: boolean;
}

interface WalletContextType {
  provider: ethers.BrowserProvider | null;
  signer: ethers.Signer | null;
  account: string;
  isConnected: boolean;
  walletKind: 'evm' | 'stellar' | null;
  connectWallet: () => Promise<void>;
  connectWalletWithProvider: (provider: any, providerName: string) => Promise<void>;
  connectFreighter: () => Promise<void>;
  signStellarTransaction: (
    xdr: string,
    opts?: { networkPassphrase?: string; address?: string; network?: string }
  ) => Promise<{ signedTxXdr?: string; error?: { message?: string } } | string>;
  disconnectWallet: () => void;
  error: string;
  loading: boolean;
  onAccountChange: (callback: (newAccount: string) => void) => () => void;
  currentProvider: any; // The actual provider being used
  providerName: string; // Name of the provider (MetaMask, Phantom, etc.)
  getAvailableProviders: () => WalletProviderInfo[]; // Get all available providers
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const [account, setAccount] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true); // Start with loading true for auto-reconnect
  const [accountChangeCallbacks, setAccountChangeCallbacks] = useState<Set<(newAccount: string) => void>>(new Set());
  const [localProvider, setLocalProvider] = useState<any>(null);
  const [providerName, setProviderName] = useState<string>('');
  const [walletKind, setWalletKind] = useState<'evm' | 'stellar' | null>(null);

  // Helper function to detect wallet provider type
  const detectWalletType = useCallback((provider: any): string => {
    if (!provider) return 'Unknown Wallet';
    
    // Check for specific wallet identifiers
    if (provider.isMetaMask) return 'MetaMask';
    if (provider.isPhantom) return 'Phantom';
    if (provider.isCoinbaseWallet) return 'Coinbase Wallet';
    if (provider.isBraveWallet) return 'Brave Wallet';
    if (provider.isTrust) return 'Trust Wallet';
    if (provider.isTokenPocket) return 'TokenPocket';
    if (provider.isBitKeep) return 'BitKeep';
    if (provider.isOKXWallet) return 'OKX Wallet';
    if (provider.isRabby) return 'Rabby';
    if (provider.isFrame) return 'Frame';
    if (provider.isTally) return 'Tally';
    if (provider.isRainbow) return 'Rainbow';
    if (provider.isWalletConnect) return 'WalletConnect';
    if (provider.isImToken) return 'imToken';
    if (provider.isTokenary) return 'Tokenary';
    if (provider.isAlphaWallet) return 'Alpha Wallet';
    if (provider.isAToken) return 'AToken';
    if (provider.isBitpie) return 'Bitpie';
    if (provider.isBlockWallet) return 'BlockWallet';
    if (provider.isBrave) return 'Brave';
    if (provider.isDapper) return 'Dapper';
    if (provider.isDefiant) return 'Defiant';
    if (provider.isFrame) return 'Frame';
    if (provider.isHuobiWallet) return 'Huobi Wallet';
    if (provider.isHyperPay) return 'HyperPay';
    if (provider.isKuCoinWallet) return 'KuCoin Wallet';
    if (provider.isMathWallet) return 'Math Wallet';
    if (provider.isMEW) return 'MyEtherWallet';
    if (provider.isOpera) return 'Opera';
    if (provider.isOwnbit) return 'Ownbit';
    if (provider.isStatus) return 'Status';
    if (provider.isTorus) return 'Torus';
    if (provider.isTrezor) return 'Trezor';
    if (provider.isWalletIO) return 'Wallet.io';
    if (provider.isXDEFI) return 'XDEFI';
    if (provider.isZerion) return 'Zerion';
    
    // Enhanced Phantom detection - check for Phantom-specific properties
    if (provider._phantom || provider.phantom || provider.isPhantom) return 'Phantom';
    
    // Check for provider-specific properties or methods
    if (provider.isConnected) return 'Connected Wallet';
    if (provider.selectedAddress) return 'Generic Wallet';
    if (provider.chainId) return 'EVM Wallet';
    
    // Check for common wallet names in the provider object
    const providerString = JSON.stringify(provider).toLowerCase();
    if (providerString.includes('metamask')) return 'MetaMask (Generic)';
    if (providerString.includes('phantom')) return 'Phantom (Generic)';
    if (providerString.includes('coinbase')) return 'Coinbase (Generic)';
    if (providerString.includes('trust')) return 'Trust (Generic)';
    if (providerString.includes('brave')) return 'Brave (Generic)';
    if (providerString.includes('opera')) return 'Opera (Generic)';
    
    return 'Unknown Wallet';
  }, []);

  // Helper function to detect and select the best available provider
  const detectProvider = useCallback(() => {
    if (typeof window.ethereum === 'undefined') {
      console.log('No wallet provider detected');
      return { provider: null, name: '', error: 'No wallet provider detected' };
    }

    // Check for multiple providers
    const ethereumProviders = (window as any).ethereum?.providers;
    
    if (ethereumProviders && Array.isArray(ethereumProviders)) {
      console.log('Multiple providers detected:', ethereumProviders.length);
      // Multiple providers detected - let user choose or use the first available
      const availableProviders = ethereumProviders.filter((p: any) => p && typeof p.request === 'function');
      
      if (availableProviders.length === 0) {
        console.log('No valid providers found');
        return { provider: null, name: '', error: 'No valid providers found' };
      }
      
      // Log all available providers for debugging
      console.log('Available providers:', availableProviders.map((p: any, index: number) => ({
        index,
        name: detectWalletType(p),
        isMetaMask: (p as any).isMetaMask,
        isPhantom: (p as any).isPhantom,
        isCoinbaseWallet: (p as any).isCoinbaseWallet
      })));
      
      // Try to find a preferred provider (MetaMask first, then others)
      const metamaskProvider = availableProviders.find((p: any) => (p as any).isMetaMask);
      const phantomProvider = availableProviders.find((p: any) => (p as any).isPhantom);
      const coinbaseProvider = availableProviders.find((p: any) => (p as any).isCoinbaseWallet);
      const braveProvider = availableProviders.find((p: any) => (p as any).isBraveWallet);
      const trustProvider = availableProviders.find((p: any) => (p as any).isTrust);
      
      if (metamaskProvider) {
        console.log('Selected MetaMask provider');
        return { provider: metamaskProvider, name: 'MetaMask' };
      } else if (phantomProvider) {
        console.log('Selected Phantom provider');
        return { provider: phantomProvider, name: 'Phantom' };
      } else if (coinbaseProvider) {
        console.log('Selected Coinbase Wallet provider');
        return { provider: coinbaseProvider, name: 'Coinbase Wallet' };
      } else if (braveProvider) {
        console.log('Selected Brave Wallet provider');
        return { provider: braveProvider, name: 'Brave Wallet' };
      } else if (trustProvider) {
        console.log('Selected Trust Wallet provider');
        return { provider: trustProvider, name: 'Trust Wallet' };
      } else {
        // Use the first available provider and detect its type
        const firstProvider = availableProviders[0];
        const providerName = detectWalletType(firstProvider);
        console.log('Selected first available provider:', providerName);
        return { provider: firstProvider, name: providerName };
      }
    } else {
      // Single provider
      const provider = window.ethereum;
      const providerName = detectWalletType(provider);
      console.log('Single provider detected:', providerName);
      return { provider, name: providerName };
    }
  }, [detectWalletType]);

  // Function to get all available providers
  const getAvailableProviders = useCallback((): WalletProviderInfo[] => {
    const providers: WalletProviderInfo[] = [];
    
    // Check for window.ethereum
    if (typeof window.ethereum !== 'undefined') {
      const ethereumProviders = (window as any).ethereum?.providers;
      
      // Debug logging
      console.log('Ethereum providers detected:', {
        hasProviders: !!ethereumProviders,
        isArray: Array.isArray(ethereumProviders),
        providersCount: ethereumProviders?.length || 0,
        singleProvider: window.ethereum
      });
      
      if (ethereumProviders && Array.isArray(ethereumProviders)) {
        const availableProviders = ethereumProviders
          .filter((p: any) => p && typeof p.request === 'function')
          .map((provider: any) => {
            const detectedName = detectWalletType(provider);
            console.log('Provider detected:', {
              provider,
              detectedName,
              isPhantom: provider.isPhantom,
              _phantom: provider._phantom,
              phantom: provider.phantom,
              keys: Object.keys(provider).slice(0, 10) // First 10 keys for debugging
            });
            return {
              provider,
              name: detectedName,
              isAvailable: true
            };
          });
        
        providers.push(...availableProviders);
      } else {
        // Single provider
        const singleProvider = {
          provider: window.ethereum,
          name: detectWalletType(window.ethereum),
          isAvailable: true
        };
        console.log('Single provider detected:', singleProvider);
        providers.push(singleProvider);
      }
    }
    
    // Check for Phantom as a separate global object (Solana/Ethereum)
    if (typeof (window as any).phantom !== 'undefined') {
      console.log('Phantom detected as separate global object');
      // Check if it has Ethereum support
      if ((window as any).phantom.ethereum) {
        providers.push({
          provider: (window as any).phantom.ethereum,
          name: 'Phantom',
          isAvailable: true
        });
      } else {
        providers.push({
          provider: (window as any).phantom,
          name: 'Phantom',
          isAvailable: true
        });
      }
    }
    
    // Check for other wallet-specific global objects (only if not already detected via ethereum)
    const walletGlobals = [
      { name: 'Coinbase', global: 'coinbaseWalletExtension' },
      { name: 'Brave', global: 'braveSolana' },
      { name: 'Trust', global: 'trustwallet' },
    ];
    
    walletGlobals.forEach(({ name, global }) => {
      if (typeof (window as any)[global] !== 'undefined') {
        // Only add if we haven't already detected this wallet through ethereum
        const alreadyDetected = providers.some(p => p.name === name);
        if (!alreadyDetected) {
          console.log(`${name} detected as separate global object`);
          providers.push({
            provider: (window as any)[global],
            name: name,
            isAvailable: true
          });
        }
      }
    });
    
    // Remove duplicates based on provider identity and name
    const uniqueProviders = providers.reduce((acc: WalletProviderInfo[], current) => {
      // Check if we already have a provider with the same name OR the same provider object
      const exists = acc.some(existing => 
        existing.name === current.name || 
        existing.provider === current.provider
      );
      
      if (!exists) {
        acc.push(current);
      } else {
        console.log(`Duplicate provider removed: ${current.name} (same as existing: ${acc.find(e => e.name === current.name || e.provider === current.provider)?.name})`);
      }
      
      return acc;
    }, []);
    
    console.log('All available providers (deduplicated):', uniqueProviders);

    // Freighter (Stellar) — always list; availability checked at connect time
    if (!uniqueProviders.some(p => p.name === 'Freighter')) {
      uniqueProviders.push({
        provider: { __stellar: true, name: 'Freighter' },
        name: 'Freighter',
        isAvailable: true,
      });
    }

    return uniqueProviders;
  }, [detectWalletType]);

  const connectFreighter = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const freighter = await import('@stellar/freighter-api');
      const connected = await freighter.isConnected();
      if (connected.error || !connected.isConnected) {
        throw new Error('Freighter is not installed or not available');
      }

      const allowed = await freighter.isAllowed();
      if (allowed.error || !allowed.isAllowed) {
        const access = await freighter.requestAccess();
        if (access.error || !access.address) {
          throw new Error(access.error?.message ?? 'Freighter access denied');
        }
      }

      const addr = await freighter.getAddress();
      if (addr.error || !addr.address) {
        throw new Error(addr.error?.message ?? 'Could not read Freighter address');
      }

      setProvider(null);
      setSigner(null);
      setLocalProvider({ __stellar: true, name: 'Freighter' });
      setProviderName('Freighter');
      setWalletKind('stellar');
      setAccount(addr.address);
      setIsConnected(true);
      localStorage.setItem('walletConnected', 'true');
      localStorage.setItem('walletProvider', 'Freighter');
      localStorage.setItem('walletKind', 'stellar');
    } catch (err: any) {
      setError(err.message || 'Failed to connect Freighter');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signStellarTransaction = useCallback(
    async (
      xdr: string,
      opts?: { networkPassphrase?: string; address?: string; network?: string }
    ) => {
      const freighter = await import('@stellar/freighter-api');
      return freighter.signTransaction(xdr, opts);
    },
    []
  );

  const connectWalletWithProvider = useCallback(async (targetProvider: any, providerName: string) => {
    // Freighter is not an EIP-1193 provider — route to the Stellar connect path
    if (providerName === 'Freighter' || targetProvider?.__stellar) {
      await connectFreighter();
      return;
    }

    try {
      setLoading(true);
      setError('');

      let accounts: string[] = [];
      
      // Try multiple connection methods for compatibility
      try {
        // Method 1: Direct request on the target provider
        accounts = await targetProvider.request({ method: 'eth_requestAccounts' });
      } catch (directError: any) {
        // Method 2: Through ethers.js provider (fallback)
        const browserProvider = new ethers.BrowserProvider(targetProvider);
        accounts = await browserProvider.send('eth_requestAccounts', []);
      }
      
      if (!accounts || accounts.length === 0) {
        setError('No accounts found. Please unlock your wallet.');
        setLoading(false);
        return;
      }
      
      // Now create the provider with the connected wallet
      const browserProvider = new ethers.BrowserProvider(targetProvider);
      const signer = await browserProvider.getSigner();
      
      // Check if current network is supported
      const network = await browserProvider.getNetwork();
      const chainId = Number(network.chainId);
      
      // Set the current provider and name
      setLocalProvider(targetProvider);
      setProviderName(providerName);
      setWalletKind('evm');
      
      setProvider(browserProvider);
      setSigner(signer);
      setAccount(accounts[0]);
      setIsConnected(true);
      
      // Network detection is now handled by NetworkContext
      
      // Save connection state to localStorage with provider info
      localStorage.setItem('walletConnected', 'true');
      localStorage.setItem('walletProvider', providerName);
      localStorage.setItem('walletKind', 'evm');
      
      // Debug logging
      console.log('Wallet connected successfully:', {
        provider: providerName,
        account: accounts[0],
        chainId: chainId,
        availableProviders: getAvailableProviders().map(p => p.name)
      });
      
      // Network support is now checked by NetworkContext
    } catch (err: any) {
      if (err.code === 4001) {
        setError('Connection request rejected. Please try again.');
      } else if (err.code === -32002) {
        setError('Connection request already pending. Please check your wallet.');
      } else {
        setError('Failed to connect wallet: ' + (err.message || 'Unknown error') + '. Try refreshing the page or checking your wallet settings.');
      }
      throw err; // Re-throw to allow calling code to handle
    } finally {
      setLoading(false);
    }
  }, [getAvailableProviders, connectFreighter]);

  const connectWallet = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Detect the best available provider
      const { provider: targetProvider, name: providerName, error: providerError } = detectProvider();
      
      if (providerError || !targetProvider) {
        setError(providerError || 'No wallet provider available');
        setLoading(false);
        return;
      }

      // Use the new connectWalletWithProvider function
      await connectWalletWithProvider(targetProvider, providerName);
    } catch (err: any) {
      if (err.code === 4001) {
        setError('Connection request rejected. Please try again.');
      } else if (err.code === -32002) {
        setError('Connection request already pending. Please check your wallet.');
      } else {
        setError('Failed to connect wallet: ' + (err.message || 'Unknown error') + '. Try refreshing the page or checking your wallet settings.');
      }
    } finally {
      setLoading(false);
    }
  }, [detectProvider, connectWalletWithProvider]);

  const disconnectWallet = useCallback(() => {
    setProvider(null);
    setSigner(null);
    setAccount('');
    setIsConnected(false);
    setError('');
    setLocalProvider(null);
    setProviderName('');
    setWalletKind(null);
    // Clear the connection state from localStorage
    localStorage.removeItem('walletConnected');
    localStorage.removeItem('walletProvider');
    localStorage.removeItem('walletKind');
  }, []);

  const onAccountChange = useCallback((callback: (newAccount: string) => void) => {
    setAccountChangeCallbacks(prev => new Set(prev).add(callback));
    
    // Return unsubscribe function
    return () => {
      setAccountChangeCallbacks(prev => {
        const newSet = new Set(prev);
        newSet.delete(callback);
        return newSet;
      });
    };
  }, []);

  // Auto-reconnect on page load
  const autoReconnect = useCallback(async () => {
    try {
      const wasConnected = localStorage.getItem('walletConnected') === 'true';
      const savedProviderName = localStorage.getItem('walletProvider');
      const savedWalletKind = localStorage.getItem('walletKind');

      if (!wasConnected) {
        return;
      }

      if (savedProviderName === 'Freighter' || savedWalletKind === 'stellar') {
        try {
          await connectFreighter();
        } catch {
          localStorage.removeItem('walletConnected');
          localStorage.removeItem('walletProvider');
          localStorage.removeItem('walletKind');
        }
        return;
      }

      if (typeof window.ethereum !== 'undefined') {
          // Detect the correct provider based on saved preference
          const { provider: targetProvider, name: providerName } = detectProvider();
          
          if (!targetProvider) {
            localStorage.removeItem('walletConnected');
            localStorage.removeItem('walletProvider');
            localStorage.removeItem('walletKind');
            return;
          }
          
          // If we have a saved provider name, try to match it
          if (savedProviderName && providerName !== savedProviderName) {
            // Provider mismatch - clear saved state
            localStorage.removeItem('walletConnected');
            localStorage.removeItem('walletProvider');
            localStorage.removeItem('walletKind');
            return;
          }
          
          // Try to reconnect without requesting accounts (silent connection)
          const accounts = await targetProvider.request({ method: 'eth_accounts' });
          
          if (!accounts || accounts.length === 0) {
            localStorage.removeItem('walletConnected');
            localStorage.removeItem('walletProvider');
            localStorage.removeItem('walletKind');
          } else {
            // Create provider and signer
            const browserProvider = new ethers.BrowserProvider(targetProvider);
            const signer = await browserProvider.getSigner();
            
            // Set the current provider and name
            setLocalProvider(targetProvider);
            setProviderName(providerName);
            setWalletKind('evm');
            
            // Set connection state - network support is checked by NetworkContext
            setProvider(browserProvider);
            setSigner(signer);
            setAccount(accounts[0]);
            setIsConnected(true);
            setError(''); // Clear any previous errors
            localStorage.setItem('walletKind', 'evm');
          }
      }
    } catch (err) {
      // Silent fail for auto-reconnect
      localStorage.removeItem('walletConnected');
      localStorage.removeItem('walletProvider');
      localStorage.removeItem('walletKind');
    } finally {
      setLoading(false);
    }
  }, [detectProvider, connectFreighter]);

  // Auto-reconnect on component mount
  useEffect(() => {
    autoReconnect();
  }, [autoReconnect]);

  // Listen for account changes
  useEffect(() => {
    if (localProvider && !localProvider.__stellar) {
      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length === 0) {
          disconnectWallet();
        } else {
          const newAccount = accounts[0];
          // Update account without full reconnection
          setAccount(newAccount);
          if (provider) {
            provider.getSigner().then(signer => {
              setSigner(signer);
            });
          }
          
          // Notify all registered callbacks about the account change
          accountChangeCallbacks.forEach(callback => {
            try {
              callback(newAccount);
            } catch (error) {
              console.error('Error in account change callback:', error);
            }
          });
        }
      };

      const handleChainChanged = async (chainId: string) => {
        // Clear any previous network error when chain changes
        setError('');
        
        // Parse the chain ID
        const parsedChainId = parseInt(chainId, 16);
        
        if (isNaN(parsedChainId) || parsedChainId <= 0) {
          setError(`Invalid chain ID received: ${chainId}`);
          return;
        }
        
        // Network support is now checked by NetworkContext
      };

      localProvider.on('accountsChanged', handleAccountsChanged);
      localProvider.on('chainChanged', handleChainChanged);

      return () => {
        if (localProvider) {
          localProvider.removeListener('accountsChanged', handleAccountsChanged);
          localProvider.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, [disconnectWallet, provider, accountChangeCallbacks, localProvider]);

  const value: WalletContextType = {
    provider,
    signer,
    account,
    isConnected,
    walletKind,
    connectWallet,
    connectWalletWithProvider,
    connectFreighter,
    signStellarTransaction,
    disconnectWallet,
    error,
    loading,
    onAccountChange,
    currentProvider: localProvider,
    providerName,
    getAvailableProviders,
  };

  return (
    <WalletContext.Provider value={value}>
      <NetworkProvider walletProvider={localProvider}>
        {children}
      </NetworkProvider>
    </WalletContext.Provider>
  );
};
